import { PLANS, PLAN_IDS, type PlanId } from '@qr/shared/plans';
import { Button, Callout, Field, Spinner } from '@qr/ui';
import { collection, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router';
import { api, messageFromError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { db } from '../lib/firebase.js';
import { useConfirmar } from '../lib/dialogos.js';

/**
 * Administracion del servicio.
 *
 * Se listan las organizaciones y se actua sobre ellas desde la propia fila.
 * Antes habia que escribir a mano el identificador de la organizacion, que
 * solo se podia conseguir abriendo la consola de Firestore: eso no es una
 * interfaz, es pedirle al operador que haga de base de datos.
 *
 * Aqui vive tambien el plan de cortesia, con el que una cuenta queda en uso
 * libre y sin cobro.
 *
 * Que la use una sola persona no la hace menos importante: es la unica
 * pantalla donde un clic apaga los codigos ya impresos de un cliente. Por eso
 * lo destructivo se distingue a simple vista de lo que no lo es.
 */

interface OrgRow {
  readonly id: string;
  readonly name: string;
  readonly plan: PlanId;
  readonly status: 'active' | 'suspended';
  readonly ownerUid: string;
  readonly ownerEmail: string | null;
  readonly compedReason?: string | null;
  readonly createdAt?: Date | null;
}

export function Superadmin() {
  const { isSuperadmin, loading } = useAuth();
  const [orgs, setOrgs] = useState<OrgRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (!isSuperadmin) return;

    return onSnapshot(
      collection(db, 'orgs'),
      async (snap) => {
        const rows = await Promise.all(
          snap.docs.map(async (d) => {
            const data = d.data();
            const ownerUid = (data['ownerUid'] as string) ?? '';

            // El correo vive en el perfil, no en la organizacion. Se lee aqui
            // porque es lo unico que permite reconocer una cuenta de un
            // vistazo; el nombre de la organizacion se genera solo y puede
            // repetirse entre clientes distintos.
            let ownerEmail: string | null = null;
            if (ownerUid) {
              try {
                const userSnap = await getDoc(doc(db, `users/${ownerUid}`));
                ownerEmail = (userSnap.data()?.['email'] as string) ?? null;
              } catch {
                ownerEmail = null;
              }
            }

            return {
              id: d.id,
              name: (data['name'] as string) ?? '(sin nombre)',
              plan: (data['plan'] as PlanId) ?? 'free',
              status: (data['status'] as 'active' | 'suspended') ?? 'active',
              ownerUid,
              ownerEmail,
              compedReason: (data['compedReason'] as string | null) ?? null,
              createdAt: data['createdAt']?.toDate?.() ?? null,
            } satisfies OrgRow;
          }),
        );

        rows.sort(
          (a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0),
        );
        setOrgs(rows);
        setError(null);
      },
      (caught) => {
        setError(messageFromError(caught));
        setOrgs([]);
      },
    );
  }, [isSuperadmin]);

  const visible = useMemo(() => {
    if (!orgs) return null;
    const needle = filter.trim().toLowerCase();
    if (!needle) return orgs;
    return orgs.filter(
      (org) =>
        org.name.toLowerCase().includes(needle) ||
        (org.ownerEmail ?? '').toLowerCase().includes(needle) ||
        org.id.toLowerCase().includes(needle),
    );
  }, [orgs, filter]);

  if (loading) return null;
  if (!isSuperadmin) return <Navigate to="/" replace />;

  const suspendidas = orgs?.filter((o) => o.status === 'suspended').length ?? 0;

  return (
    <div className="flex flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3 lg:px-6">
        <h1 className="m-0 text-lg font-semibold tracking-tight">Administración</h1>
        {orgs ? (
          <span className="rounded-full border border-line px-2 py-0.5 text-[11.5px] text-ink-3 tabular-nums">
            {orgs.length}
          </span>
        ) : null}
        {suspendidas > 0 ? (
          <span className="badge badge--danger">
            {suspendidas} suspendida{suspendidas === 1 ? '' : 's'}
          </span>
        ) : null}

        {(orgs?.length ?? 0) > 0 ? (
          <label className="ms-auto flex items-center gap-2 rounded-sm border border-line bg-surface px-3 py-1.5 text-ink-3 focus-within:border-brand">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <circle cx="7" cy="7" r="4.6" stroke="currentColor" strokeWidth="1.5" />
              <path
                d="M10.6 10.6L14 14"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <input
              id="buscar-cuentas"
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Nombre, correo o identificador"
              className="w-48 border-0 bg-transparent p-0 text-[13.5px] text-ink outline-none placeholder:text-ink-3 sm:w-64"
            />
            <span className="sr-only">Buscar cuentas</span>
          </label>
        ) : null}
      </header>

      {/* Un recordatorio permanente, no un aviso que se cierra: cada vez que
          entras aquí estás a un clic de apagar los códigos de un cliente. */}
      <p className="flex items-center gap-2 border-b border-line bg-warn-soft px-4 py-2 text-[12.5px] text-warn lg:px-6">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M8 2.2L14.6 13.4H1.4L8 2.2z"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinejoin="round"
          />
          <path d="M8 6.4v3.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="8" cy="11.4" r="0.85" fill="currentColor" />
        </svg>
        Lo que hagas aquí afecta a cuentas de clientes y queda registrado con tu
        nombre en el historial de auditoría.
      </p>

      <div className="flex max-w-[64rem] flex-col gap-5 px-4 py-5 lg:px-6">
        {error ? <Callout tone="danger">{error}</Callout> : null}

        {visible === null ? (
          <div className="center">
            <Spinner label="Cargando cuentas" />
          </div>
        ) : visible.length === 0 ? (
          <p className="rounded border border-dashed border-line px-4 py-10 text-center text-ink-2">
            {filter
              ? 'Ninguna cuenta coincide con la búsqueda.'
              : 'Todavía no hay cuentas.'}
          </p>
        ) : (
          <ul className="flex list-none flex-col gap-2 p-0">
            {visible.map((org) => (
              <OrgItem key={org.id} org={org} />
            ))}
          </ul>
        )}

        <Maintenance />
      </div>
    </div>
  );
}

function OrgItem({ org }: { readonly org: OrgRow }) {
  const [open, setOpen] = useState(false);
  const isComped = PLANS[org.plan].monthlyPriceUsdCents === null;
  const suspendida = org.status === 'suspended';

  return (
    <li
      className={[
        'rounded-lg border bg-surface',
        // Una cuenta suspendida se ve suspendida desde la lista, sin abrirla:
        // es un estado que hay que poder detectar de un vistazo.
        suspendida ? 'border-bad/45' : 'border-line',
      ].join(' ')}
    >
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <div className="min-w-0 flex-1">
          <strong className="block truncate text-[14px]">{org.name}</strong>
          <span className="block truncate text-[12.5px] text-ink-3">
            {org.ownerEmail ?? org.ownerUid}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className={`badge${isComped ? ' badge--success' : ''}`}>
            {PLANS[org.plan].name}
          </span>
          {suspendida ? <span className="badge badge--danger">Suspendida</span> : null}
        </div>

        <Button size="sm" variant="secondary" onClick={() => setOpen(!open)}>
          {open ? 'Cerrar' : 'Gestionar'}
        </Button>
      </div>

      {org.compedReason ? (
        <p className="m-0 border-t border-line px-4 py-2 text-[12px] text-ink-3">
          Cortesía: {org.compedReason}
        </p>
      ) : null}

      {open ? <OrgActions org={org} /> : null}
    </li>
  );
}

function OrgActions({ org }: { readonly org: OrgRow }) {
  const confirmar = useConfirmar();
  const [plan, setPlan] = useState<PlanId>(org.plan);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<'plan' | 'status' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const planChanged = plan !== org.plan;
  const reasonOk = reason.trim().length >= 3;
  const suspending = org.status === 'active';

  async function applyPlan() {
    setError(null);
    setDone(null);
    setBusy('plan');
    try {
      const result = await api.changePlan({
        orgId: org.id,
        plan,
        reason: reason.trim(),
      });
      setDone(`Plan cambiado a ${result.plan}.`);
      setReason('');
    } catch (caught) {
      setError(messageFromError(caught));
    } finally {
      setBusy(null);
    }
  }

  async function toggleStatus() {
    const next = suspending ? 'suspended' : 'active';
    const confirmed = await confirmar(
      suspending
        ? {
            titulo: `Suspender "${org.name}"`,
            mensaje:
              'Todos sus códigos QR dejarán de redirigir en menos de un minuto, incluidos los ya impresos.',
            aceptar: 'Suspender',
            grave: true,
          }
        : {
            titulo: `Reactivar "${org.name}"`,
            mensaje: 'Sus códigos volverán a funcionar.',
            aceptar: 'Reactivar',
          },
    );
    if (!confirmed) return;

    setError(null);
    setDone(null);
    setBusy('status');
    try {
      await api.setOrgStatus({ orgId: org.id, status: next, reason: reason.trim() });
      setDone(suspending ? 'Cuenta suspendida.' : 'Cuenta reactivada.');
      setReason('');
    } catch (caught) {
      setError(messageFromError(caught));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex flex-col gap-4 border-t border-line bg-ground px-4 py-4">
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-[12px] text-ink-3">
        <span>
          Identificador <code className="font-mono">{org.id}</code>
        </span>
        {org.createdAt ? (
          <span>Alta el {org.createdAt.toLocaleDateString('es')}</span>
        ) : null}
      </div>

      {/* El motivo lo exigen las dos acciones, asi que se pide una sola vez. */}
      <Field
        label="Motivo"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        minLength={3}
        maxLength={300}
        placeholder="Ej: cliente de confianza, acceso gratuito indefinido"
        hint="Obligatorio. Queda guardado para poder consultarlo más adelante."
        error={error}
      />

      {/* Cambiar el plan y suspender no son lo mismo, y antes se veian igual:
          dos botones uno al lado del otro. Suspender apaga los codigos ya
          impresos de un cliente, asi que vive aparte y con su propio marco. */}
      <div className="flex flex-wrap items-end gap-3 rounded border border-line bg-surface p-3.5">
        <div className="qr-field min-w-[12rem] flex-1">
          <label className="qr-field__label" htmlFor={`plan-${org.id}`}>
            Plan
          </label>
          <select
            id={`plan-${org.id}`}
            className="qr-field__input"
            value={plan}
            onChange={(e) => setPlan(e.target.value as PlanId)}
          >
            {PLAN_IDS.map((id) => (
              <option key={id} value={id}>
                {PLANS[id].name}
                {PLANS[id].monthlyPriceUsdCents === null ? ' (sin cobro)' : ''}
              </option>
            ))}
          </select>
        </div>

        <Button
          onClick={applyPlan}
          loading={busy === 'plan'}
          disabled={!planChanged || !reasonOk || busy !== null}
        >
          Aplicar plan
        </Button>
      </div>

      {PLANS[plan].monthlyPriceUsdCents === null && planChanged ? (
        <Callout tone="info" title="Cuenta de cortesía">
          Quedará sin límites y su panel no mostrará medidores de consumo ni
          invitaciones a mejorar de plan.
        </Callout>
      ) : null}

      <div
        className={[
          'flex flex-wrap items-center gap-3 rounded border p-3.5',
          suspending ? 'border-bad/40 bg-bad-soft/40' : 'border-line bg-surface',
        ].join(' ')}
      >
        <p className="m-0 min-w-[14rem] flex-1 text-[12.5px] text-ink-2">
          {suspending
            ? 'Suspender apaga todos sus códigos, incluidos los ya impresos. Deja de redirigir en menos de un minuto.'
            : 'Reactivar devuelve el servicio: sus códigos vuelven a redirigir enseguida.'}
        </p>
        <Button
          variant={suspending ? 'danger' : 'secondary'}
          onClick={toggleStatus}
          loading={busy === 'status'}
          disabled={!reasonOk || busy !== null}
        >
          {suspending ? 'Suspender cuenta' : 'Reactivar cuenta'}
        </Button>
      </div>

      {!reasonOk ? (
        <p className="m-0 text-[12.5px] text-ink-3">
          Escribe el motivo para poder aplicar cualquiera de las dos acciones.
        </p>
      ) : null}

      {done ? <span className="saved">{done}</span> : null}
    </div>
  );
}

/**
 * Mantenimiento puntual.
 *
 * Va replegado y al final: son herramientas que se usan una vez cada mucho, y
 * arriba le robaban el sitio a las cuentas, que es a lo que se entra aqui.
 */
function Maintenance() {
  const confirmar = useConfirmar();
  const [busy, setBusy] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  return (
    <details className="mt-2 rounded border border-line px-4 py-3 text-[13px]">
      <summary className="cursor-pointer text-ink-2">Mantenimiento</summary>

      <div className="flex flex-col gap-5 pt-4">
        {error ? <Callout tone="danger">{error}</Callout> : null}
        {result ? <Callout tone="success">{result}</Callout> : null}

        <div className="flex flex-col items-start gap-2">
          <strong className="text-[13.5px]">Completar datos de miembros</strong>
          <p className="m-0 text-ink-2">
            Añade los campos que necesita la consulta de espacios a los miembros
            antiguos. Se puede ejecutar las veces que haga falta: solo toca lo que
            esté incompleto.
          </p>
          <Button
            variant="secondary"
            size="sm"
            loading={busy}
            onClick={async () => {
              setError(null);
              setResult(null);
              setBusy(true);
              try {
                const r = await api.backfillMemberships({});
                setResult(
                  `${r.revisados} miembros revisados, ${r.corregidos} corregidos.`,
                );
              } catch (caught) {
                setError(messageFromError(caught));
              } finally {
                setBusy(false);
              }
            }}
          >
            Ejecutar
          </Button>
        </div>

        <div className="flex flex-col items-start gap-2 border-t border-line pt-4">
          <strong className="text-[13.5px]">Eliminar negocios sin dueño</strong>
          <p className="m-0 text-ink-2">
            Borrar una cuenta en Firebase Auth no borra sus datos: su espacio, sus
            códigos y sus enlaces cortos siguen ocupados. Esto elimina los espacios
            cuyo propietario ya no existe. Los que sí tienen dueño no se tocan.
          </p>
          <Button
            variant="danger"
            size="sm"
            loading={cleaning}
            onClick={async () => {
              const ok = await confirmar({
                titulo: 'Limpiar negocios huérfanos',
                mensaje:
                  'Se eliminarán los negocios cuyo propietario ya no exista, con sus códigos y sus enlaces cortos. Esto no se puede deshacer.',
                aceptar: 'Limpiar',
                grave: true,
              });
              if (!ok) return;
              setError(null);
              setResult(null);
              setCleaning(true);
              try {
                const r = await api.cleanupOrphanOrgs({});
                setResult(
                  `${r.revisadas} espacios revisados, ${r.borradas} eliminados, ` +
                    `${r.perfilesBorrados} perfiles huérfanos borrados.`,
                );
              } catch (caught) {
                setError(messageFromError(caught));
              } finally {
                setCleaning(false);
              }
            }}
          >
            Eliminar negocios sin dueño
          </Button>
        </div>
      </div>
    </details>
  );
}
